import { Outlet, useParams, useNavigate } from "react-router-dom";
import { useDateFromSearchParams } from "@/helpers/useTimeHooks";
import { Button, Card, NativeSelect, Typography } from "@altimateai/lego";
import { DateRange } from "@/components/dateRange";
import { useQuery } from "@tanstack/react-query";
import {
  getDbtProjectAndIntegrationDetails,
  getSnowflakeJobsSummary,
  getSnowflakeJobGraphData,
  getDbtHealthChecks,
  getDbtTasksSummary,
} from "@/helpers/apis";
import { formatBytes, formatMilliseconds, formatCost } from "@/helpers/utils";
import { useAppState } from "@/modules/app/useAppContext";
import {
  getDateByEndOfDayWithoutTimeoffset,
  getDateByStartOfDayWithoutTimeoffset,
} from "@/components/dateRange/utils";
import { WorkflowType } from "@/pages/query/types";
import { IconChevronLeft } from "@tabler/icons-react";
import { formatNumber, Chart, ChartTypes } from "@/uiCore";
import { Stack } from "@altimateai/lego";
import { useState, useMemo } from "react";
import { Bar, Rectangle, TooltipProps, Line } from "recharts";
import dayjs from "dayjs";
import { RelativeComponentLoader } from "@/components/loader";
import { CostChartTooltip } from "@/pages/query/Graph";
import { FlexListSummary } from "@/components/listSummary";
import { useBreadcrumb } from "@/components/breadcrumbs";

const ChartOptions = [
  { label: "Cost", value: "cost" },
  { label: "Test Coverage", value: "test_coverage" },
];

export const DbtJob = () => {
  const { workflowId } = useParams();
  const { startDate, endDate, onUpdate } = useDateFromSearchParams();
  const { currency } = useAppState();
  const navigate = useNavigate();
  const [chartType, setChartType] = useState<string>("cost");

  const projectName = workflowId ? decodeURIComponent(workflowId).split("/").pop() : null;
  useBreadcrumb(projectName ?? "dbt", {
    defaultParents: [
      { label: "Code", pathname: "/query" },
      ...(projectName ? [{ label: "dbt", pathname: "/query/dbt-jobs" }] : []),
    ],
    truncateTo: "/query/dbt-jobs",
  });

  const { data: details } = useQuery({
    queryKey: ["details", workflowId, startDate, endDate],
    queryFn: () => getDbtProjectAndIntegrationDetails(encodeURIComponent(workflowId!)),
    enabled: Boolean(workflowId),
  });

  const { data: summaryData, isLoading: isSummaryLoading } = useQuery({
    queryKey: ["dbt_job_summary", workflowId, startDate, endDate],
    queryFn: () =>
      getSnowflakeJobsSummary(encodeURIComponent(workflowId!), {
        start_date: getDateByStartOfDayWithoutTimeoffset(startDate).toISOString(),
        end_date: getDateByEndOfDayWithoutTimeoffset(endDate)?.toISOString(),
        workflow_type: WorkflowType.DBT,
      }),
    enabled: Boolean(workflowId),
  });

  const { data: tasksSummaryData, isLoading: isTasksSummaryLoading } = useQuery({
    queryKey: ["dbt_tasks_summary", workflowId, startDate, endDate],
    queryFn: () =>
      getDbtTasksSummary(encodeURIComponent(workflowId!), {
        page: 1,
        size: 10,
        start_date: getDateByStartOfDayWithoutTimeoffset(startDate).toISOString(),
        end_date: getDateByEndOfDayWithoutTimeoffset(endDate)?.toISOString(),
      }),
    enabled: Boolean(workflowId),
  });

  const { data: graphData, isLoading: isGraphLoading } = useQuery({
    queryKey: ["dbt_job_graph_data", workflowId, startDate, endDate],
    queryFn: () =>
      getSnowflakeJobGraphData({
        workflows: [workflowId!],
        start_date: getDateByStartOfDayWithoutTimeoffset(startDate).toISOString(),
        end_date: getDateByEndOfDayWithoutTimeoffset(endDate)?.toISOString(),
        workflow_type: WorkflowType.DBT,
      }),
    enabled: Boolean(workflowId) && chartType === "cost",
  });

  const { data: healthCheckData, isLoading: isHealthCheckLoading } = useQuery({
    queryKey: [
      "dbt_health_checks",
      workflowId,
      startDate,
      endDate,
      chartType,
      summaryData?.integration_id,
      summaryData?.integration_environment_id,
    ],
    queryFn: () => {
      return getDbtHealthChecks({
        name: "Low test coverage in dbt models",
        integration_id: summaryData?.integration_id
          ? parseInt(summaryData.integration_id, 10) || undefined
          : undefined,
        environment_id: summaryData?.integration_environment_id
          ? parseInt(summaryData.integration_environment_id, 10) || undefined
          : undefined,
        start_date: getDateByStartOfDayWithoutTimeoffset(startDate).toISOString(),
        end_date: getDateByEndOfDayWithoutTimeoffset(endDate)?.toISOString(),
        limit: 100,
      });
    },
    enabled:
      Boolean(workflowId) &&
      chartType === "test_coverage" &&
      Boolean(summaryData?.integration_id) &&
      Boolean(summaryData?.integration_environment_id),
  });

  const chartData = useMemo(() => {
    if (chartType === "cost") {
      if (!graphData || graphData.length === 0) return [];

      return graphData.map(entry => {
        const dateKey = entry.date;
        const workflowKey = Object.keys(entry).find(key => key !== "date");
        const workflowData = workflowKey ? entry[workflowKey] : null;

        return {
          date: dayjs(dateKey).format("MMM-DD"),
          cost: workflowData?.cost_value ?? 0,
        };
      });
    } else if (chartType === "test_coverage") {
      if (!healthCheckData?.results || healthCheckData.results.length === 0) return [];

      return healthCheckData.results.map(result => {
        const testCoverageInsight = result.project_insights.find(
          insight => insight.name === "Testing Coverage Low" || insight.type === "testing"
        );

        const coveragePercentage = testCoverageInsight?.insight_metadata?.coverage_percentage ?? 0;

        return {
          date: dayjs(result.run_timestamp).format("MMM-DD"),
          test_coverage: coveragePercentage,
        };
      });
    }

    return [];
  }, [chartType, graphData, healthCheckData]);

  const metrics = [
    // TODO: add these when available in the backend
    // { label: "Package Name", value: (details as unknown as { package_name?: string })?.package_name ?? "dbt/snowflake" },
    { label: "Total run", value: isSummaryLoading ? "-" : (summaryData?.runs ?? "-") },
    {
      label: "Integration Environment",
      value: isSummaryLoading ? (
        "-"
      ) : (
        <span>{summaryData?.integration_environment_name ?? "-"}</span>
      ),
    },
    {
      label: "Integration Name",
      value: isSummaryLoading ? "-" : <span>{summaryData?.integration_name ?? "-"}</span>,
    },
    {
      label: "Total cost",
      value: isSummaryLoading ? "-" : formatNumber(summaryData?.query_cost ?? 0, { currency }),
    },
    {
      label: "Bytes Scanned",
      value: isSummaryLoading
        ? "-"
        : formatBytes((summaryData as unknown as { bytes_scanned?: number })?.bytes_scanned ?? 0),
    },
    {
      label: "Average execution time",
      value: isSummaryLoading
        ? "-"
        : formatMilliseconds((summaryData?.execution_time ?? 0) / (summaryData?.runs || 1)),
    },
    // TODO: add these when available in the backend
    // {
    //   label: "Distinct warehouses",
    //   value: isSummaryLoading ? "-" : (summaryData as unknown as { distinct_warehouses?: number })?.distinct_warehouses ?? "-",
    // },
    // { label: "Distinct users", value: isSummaryLoading ? "-" : (summaryData as unknown as { distinct_users?: number })?.distinct_users ?? "-" },
  ];

  const summaryDataWithInfo = useMemo(() => {
    if (!summaryData) {
      return [];
    }
    return [
      {
        label: "Total Costs",
        value: formatNumber(tasksSummaryData?.total_cost ?? 0, { currency }),
      },
      {
        label: "Model Run Costs",
        value: tasksSummaryData?.model_cost
          ? formatNumber(
              tasksSummaryData?.model_cost +
                tasksSummaryData?.seed_cost +
                tasksSummaryData?.snapshot_cost,
              { currency }
            )
          : "-",
        info: "Includes costs from model, seed and snapshot tasks",
      },
      {
        label: "Test Run Costs",
        value: tasksSummaryData?.test_cost
          ? formatNumber(tasksSummaryData?.test_cost, { currency })
          : "-",
      },
      {
        label: "Test Coverage",
        value:
          tasksSummaryData && tasksSummaryData.test_coverage !== null
            ? `${tasksSummaryData.test_coverage.toFixed(1)}%`
            : "-",
        info: "Test Coverage is calculated from the latest synced dbt artifacts",
      },
    ];
  }, [summaryData, tasksSummaryData, currency]);

  return (
    <Card className="al-p-0">
      <Stack direction="row" align="start" gap="sm" className="al-bg-blue-50 p-3 border-radius-top">
        <Button
          role={"link"}
          variant={"ghost"}
          onClick={() => navigate(-1)}
          className={"al-pt-0 hover:al-bg-transparent"}
          title="Go back"
        >
          <IconChevronLeft className={"!al-size-5"} />
        </Button>
        <Stack direction="column" gap="sm" className="al-w-full">
          <Stack direction="row" gap="xs" align="center">
            {workflowId ? (
              <div className="text-primary">
                {details?.dbt_project_name || workflowId?.split("/").pop() || workflowId}
              </div>
            ) : null}
            <div className="spacer" />
          </Stack>
          <Stack
            direction="row"
            className="flex-wrap al-m-0"
            tag="dl"
            role="region"
            aria-label="dbt job metrics region"
          >
            {metrics.map((item, index) => {
              const termId = `metric-${index}-label`;
              return (
                <div key={item.label} className="me-4">
                  <Stack direction="row" gap="xs">
                    <dt id={termId} className="text-muted al-m-0">
                      {item.label}:
                    </dt>
                    <dd aria-labelledby={termId} className="al-m-0">
                      {item.value}
                    </dd>
                  </Stack>
                </div>
              );
            })}
          </Stack>
        </Stack>
      </Stack>
      <div className="al-p-4">
        <Stack direction="row" align="center" justify="end" className="mb-3">
          <DateRange
            startDate={startDate}
            endDate={endDate}
            availableStaticRanges={["Last day", "Last 7 days", "Last 28 days"]}
            onDateRangeSelect={onUpdate}
          />
        </Stack>
        <FlexListSummary
          summary={summaryDataWithInfo}
          isLoading={isSummaryLoading || isTasksSummaryLoading}
          className="al-mb-4"
        />
        <div className="al-mb-6 al-relative">
          <Stack className="al-absolute al-top-2 al-left-2 al-z-10" align="center">
            <Typography size="regular">Chart of: </Typography>
            <NativeSelect
              id="chart-selector"
              onChange={(value: unknown) => setChartType(value as string)}
              value={chartType ?? ""}
              options={ChartOptions.map(o => ({
                label: o.label,
                value: o.value,
              }))}
            />
          </Stack>
          {isGraphLoading || isHealthCheckLoading ? (
            <RelativeComponentLoader label="Loading chart data..." componentHeight={40} />
          ) : chartData.length > 0 ? (
            <div className="al-pt-16">
              <Chart
                title={chartType === "cost" ? "Cost" : "Test Coverage"}
                xAxisDataKey="date"
                yAxisLabel={{
                  value: chartType === "cost" ? "Cost" : "Test Coverage (%)",
                }}
                xAxisLabelFormatter={value => value}
                yAxisLabelFormatter={value =>
                  chartType === "cost" ? formatCost(value, currency) : `${value}%`
                }
                type={chartType === "cost" ? ChartTypes.BarChart : ChartTypes.LineChart}
                data={chartData}
                hideFullscreenButton
                tooltipProps={
                  chartType === "cost"
                    ? {
                        content: (props: TooltipProps<number, string>) => (
                          <CostChartTooltip {...props} />
                        ),
                      }
                    : {}
                }
                chartProps={{}}
              >
                {({ barSize }) => {
                  if (chartType === "cost") {
                    return (
                      <Bar
                        barSize={barSize}
                        dataKey="cost"
                        fill="#5B9FFF"
                        shape={(props: unknown) => {
                          (props as { radius: number[] }).radius = [5, 5, 0, 0];
                          return <Rectangle {...(props as Record<string, unknown>)} />;
                        }}
                      />
                    );
                  } else {
                    return (
                      <Line
                        type="monotone"
                        dataKey="test_coverage"
                        stroke="#247EFE"
                        strokeWidth={4}
                        dot={{ r: 0 }}
                        activeDot={{ r: 8 }}
                        name="Test Coverage"
                      />
                    );
                  }
                }}
              </Chart>
            </div>
          ) : (
            <div className="al-text-center al-text-gray-500 al-py-8">
              No chart data available for the selected period
            </div>
          )}
        </div>
        <Outlet />
      </div>
    </Card>
  );
};
